import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { getMetaCardTheme } from '../../utils/metaColor';
import { buildSheetRow } from '../../utils/funnelSheet';
import ClinicFunnelUnitCard from './ClinicFunnelUnitCard';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtPct = (v) => `${(Number(v) * 100).toFixed(1)}%`;

export default function ClinicFunnelUnitVisualization({ unit, report, showFunnelAndGoals = true }) {
  const [dezenaMetric, setDezenaMetric] = useState('fechamentos');

  if (!unit) return null;

  const funilUnidadeData = [
    { etapa: 'Leads', Planejado: Number(unit.plannedLeads || 0), Realizado: Number(unit.realizadoLeads || 0) },
    { etapa: 'Agendamentos', Planejado: Number(unit.plannedAgendamentos || 0), Realizado: Number(unit.realizadoAgendamentos || 0) },
    { etapa: 'Comparecimentos', Planejado: Number(unit.plannedComparecimentos || 0), Realizado: Number(unit.realizadoComparecimentos || 0) },
    { etapa: 'Fechamentos', Planejado: Number(unit.plannedFechamentos || 0), Realizado: Number(unit.realizadoFechamentos || 0) },
  ];

  const dezenasUnidadeData = (unit.dezenaBreakdown || []).map(d => {
    let planejado = 0;
    let realizado = 0;
    let big = 0;
    if (dezenaMetric === 'fechamentos') {
      planejado = Number(d.plannedFechamentos || 0);
      realizado = Number(d.realizadoFechamentos || 0);
      big = Number(d.bigFechamentos || 0);
    } else if (dezenaMetric === 'comparecimentos') {
      planejado = Number(d.plannedComparecimentos || 0);
      realizado = Number(d.realizadoComparecimentos || 0);
      big = Number(d.bigComparecimentos || 0);
    } else if (dezenaMetric === 'agendamentos') {
      planejado = Number(d.plannedAgendamentos || 0);
      realizado = Number(d.realizadoAgendamentos || 0);
      big = Number(d.bigAgendamentos || 0);
    } else if (dezenaMetric === 'leads') {
      planejado = Number(d.plannedLeads || 0);
      realizado = Number(d.realizadoLeads || 0);
      big = Number(d.bigLeads || 0);
    }
    return {
      name: d.name,
      Planejado: planejado,
      Realizado: realizado,
      BigStretch: big
    };
  });

  const getRow = (label, real, plan, isCurrency = false) => {
    if (!showFunnelAndGoals) {
      return (
        <tr className="hover:bg-slate-50">
          <td className="px-4 py-3 font-medium text-slate-800">{label}</td>
          <td className="px-4 py-3 font-bold text-slate-800">{isCurrency ? fmtBRL(real) : fmtNum(real)}</td>
        </tr>
      );
    }
    const pct = plan > 0 ? (real / plan) * 100 : 0;
    const theme = getMetaCardTheme(pct);
    return (
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-3 font-medium text-slate-800">{label}</td>
        <td className="px-4 py-3 text-slate-600">{isCurrency ? fmtBRL(plan) : fmtNum(plan)}</td>
        <td className="px-4 py-3 font-bold text-slate-800">{isCurrency ? fmtBRL(real) : fmtNum(real)}</td>
        <td className="px-4 py-3">
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${theme.bg} ${theme.label}`}>{fmtPct(pct / 100)}</span>
        </td>
      </tr>
    );
  };

  const cplReal = Number(unit.realizadoLeads) > 0 ? Number(unit.realizadoInvestimento || 0) / Number(unit.realizadoLeads) : 0;
  const cplPlan = Number(unit.plannedLeads) > 0 ? Number(unit.plannedInvestimento || 0) / Number(unit.plannedLeads) : 0;

  const custoAgendReal = Number(unit.realizadoAgendamentos) > 0 ? Number(unit.realizadoInvestimento || 0) / Number(unit.realizadoAgendamentos) : 0;
  const custoAgendPlan = Number(unit.plannedAgendamentos) > 0 ? Number(unit.plannedInvestimento || 0) / Number(unit.plannedAgendamentos) : 0;

  const custoCompReal = Number(unit.realizadoComparecimentos) > 0 ? Number(unit.realizadoInvestimento || 0) / Number(unit.realizadoComparecimentos) : 0;
  const custoCompPlan = Number(unit.plannedComparecimentos) > 0 ? Number(unit.plannedInvestimento || 0) / Number(unit.plannedComparecimentos) : 0;

  const cacReal = Number(unit.realizadoFechamentos) > 0 ? Number(unit.realizadoInvestimento || 0) / Number(unit.realizadoFechamentos) : 0;
  const cacPlan = Number(unit.plannedFechamentos) > 0 ? Number(unit.plannedInvestimento || 0) / Number(unit.plannedFechamentos) : 0;

  const cplTipoLabel = {
    fixo: 'Fixo (Meta)',
    anterior: 'Média 3 Meses',
    real: 'Real Atual'
  }[unit.cplTipo || 'fixo'] || 'Fixo';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-wrap gap-6 items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-black font-headline text-slate-800">{unit.routineName}</h2>
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
              CPL Ativo: {cplTipoLabel} ({fmtBRL(unit.cplMedio)})
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Ref: {new Date(report.referenceMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Ticket Médio</p>
            <p className="text-lg font-bold text-slate-800">{fmtBRL(unit.ticketMedio)}</p>
          </div>
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1" title="Média dos últimos 3 meses">CPL Média 3M (Ant.)</p>
            <p className="text-lg font-bold text-slate-800">{fmtBRL(unit.cplAnterior || 0)}</p>
          </div>
          {showFunnelAndGoals && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">CPL Fixo (Meta)</p>
              <p className="text-lg font-bold text-slate-800">{fmtBRL(unit.cplFixo !== undefined ? unit.cplFixo : unit.cplMedio)}</p>
            </div>
          )}
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">CPL Real Atual</p>
            <p className="text-lg font-bold text-slate-800">
              {Number(unit.realizadoLeads) > 0 ? fmtBRL(Number(unit.realizadoInvestimento || 0) / Number(unit.realizadoLeads)) : fmtBRL(0)}
            </p>
          </div>
        </div>
      </div>

      {showFunnelAndGoals && (
        <div className="max-w-3xl">
          <ClinicFunnelUnitCard row={buildSheetRow(unit, report)} referenceMonth={report.referenceMonth} />
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-headline font-bold mb-4">Eficiência de Aquisição e Custos por Etapa</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Custo por Lead (CPL)</p>
            <p className="text-xl font-black text-slate-800">{fmtBRL(cplReal)}</p>
            {showFunnelAndGoals && <p className="text-[10px] text-slate-400 mt-1">Planejado: {fmtBRL(cplPlan)}</p>}
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Custo por Agendamento</p>
            <p className="text-xl font-black text-slate-800">{fmtBRL(custoAgendReal)}</p>
            {showFunnelAndGoals && <p className="text-[10px] text-slate-400 mt-1">Planejado: {fmtBRL(custoAgendPlan)}</p>}
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Custo por Comparecimento</p>
            <p className="text-xl font-black text-slate-800">{fmtBRL(custoCompReal)}</p>
            {showFunnelAndGoals && <p className="text-[10px] text-slate-400 mt-1">Planejado: {fmtBRL(custoCompPlan)}</p>}
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Custo por Fechamento (CAC)</p>
            <p className="text-xl font-black text-slate-800">{fmtBRL(cacReal)}</p>
            {showFunnelAndGoals && <p className="text-[10px] text-slate-400 mt-1">Planejado: {fmtBRL(cacPlan)}</p>}
          </div>
        </div>
      </div>

      {/* Gráficos da Unidade (Excel) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* a) Funil da Unidade */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-1">Funil da Unidade</h3>
          <p className="text-xs text-slate-500 mb-4">Planejado vs Realizado nos 4 estágios</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funilUnidadeData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="etapa" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(val) => fmtNum(val)} />
                <Legend />
                {showFunnelAndGoals && <Bar dataKey="Planejado" fill="#94a3b8" radius={[4, 4, 0, 0]} />}
                <Bar dataKey="Realizado" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* b) Evolução por Dezena com Seletor */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <h3 className="font-headline font-bold mb-1">Evolução por Dezena (D1, D2, D3)</h3>
              <p className="text-xs text-slate-500">Ritmo temporal por estágio</p>
            </div>
            <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setDezenaMetric('fechamentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'fechamentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Fech.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('comparecimentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'comparecimentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Comp.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('agendamentos')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'agendamentos' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Agend.
              </button>
              <button
                type="button"
                onClick={() => setDezenaMetric('leads')}
                className={`px-2.5 py-1 rounded transition-colors ${dezenaMetric === 'leads' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Leads
              </button>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dezenasUnidadeData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(val) => fmtNum(val)} />
                <Legend />
                <Bar dataKey="Realizado" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                {showFunnelAndGoals && <Line type="monotone" dataKey="Planejado" stroke="#64748b" strokeWidth={2} strokeDasharray="4 4" />}
                {showFunnelAndGoals && <Line type="monotone" dataKey="BigStretch" name="Big Stretch" stroke="#10b981" strokeWidth={2} />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-headline font-bold">Funil de Vendas</h3></div>
        <table className="w-full text-left text-sm font-body">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 font-bold text-[10px] uppercase text-slate-500">Estágio</th>
              {showFunnelAndGoals && <th className="px-4 py-3 font-bold text-[10px] uppercase text-slate-500">Planejado</th>}
              <th className="px-4 py-3 font-bold text-[10px] uppercase text-slate-500">Realizado</th>
              {showFunnelAndGoals && <th className="px-4 py-3 font-bold text-[10px] uppercase text-slate-500">Atingimento</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {getRow('Receita', unit.realizadoReceita, unit.metaReceita, true)}
            {getRow('Investimento', unit.realizadoInvestimento, unit.plannedInvestimento, true)}
            {getRow('Leads', unit.realizadoLeads, unit.plannedLeads)}
            {getRow('Agendamentos', unit.realizadoAgendamentos, unit.plannedAgendamentos)}
            {getRow('Comparecimentos', unit.realizadoComparecimentos, unit.plannedComparecimentos)}
            {getRow('Fechamentos', unit.realizadoFechamentos, unit.plannedFechamentos)}
          </tbody>
        </table>
      </div>
    </div>
  );
}